/**
 * Public Near Me reads. No session is needed or sent meaningfully: these never answer 401, and a
 * 404 means "not available" (including when public events are switched off).
 */

import { apiGet } from './api';
import type { AreaPage, PublicVenuePage, RegionIndex } from '@/types/nearMePublic';

const BASE = '/api/public/near-me';
const seg = (value: string) => encodeURIComponent(value);

/** Whether the public events pages are switched on. Always answers 200, so the app can decide what to show. */
export const getPublicEventsStatus = () => apiGet<{ publicEventsEnabled: boolean }>(`${BASE}/status`, { auth: false });

export const getRegions = () => apiGet<RegionIndex>(`${BASE}/regions`, { auth: false });

export function getArea(country: string, area: string, town?: string) {
  const path = town ? `${seg(country)}/${seg(area)}/${seg(town)}` : `${seg(country)}/${seg(area)}`;
  return apiGet<AreaPage>(`${BASE}/areas/${path}`, { auth: false });
}

export const getPublicVenue = (venueId: string) => apiGet<PublicVenuePage>(`${BASE}/venues/${seg(venueId)}`, { auth: false });

const API_ORIGIN = (import.meta.env.VITE_API_URL as string | undefined) || '';

const absolute = (path: string): string => {
  const origin = API_ORIGIN || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${origin}${path}`;
};

/** Absolute URL of a calendar, for a link, copying, or a webcal:// subscription. */
export const venueCalendarUrl = (venueId: string) => absolute(`${BASE}/venues/${seg(venueId)}.ics`);

export function areaCalendarUrl(country: string, area: string, town?: string) {
  const path = town ? `${seg(country)}/${seg(area)}/${seg(town)}` : `${seg(country)}/${seg(area)}`;
  return absolute(`${BASE}/areas/${path}.ics`);
}

/** `https://host/x.ics` to `webcal://host/x.ics`, which opens the person's calendar app. */
export const toWebcal = (url: string): string => url.replace(/^https?:\/\//, 'webcal://');
